/** User-visible strings of the bank-of-changes workflow. Like the other Word
 * agent skills it always talks Hebrew (see SKILL_AGENT_LANGUAGE), so there is
 * one copy object and no English variant. */

export type BankOfChangesCopy = {
  backToWorkflows: string;
  working: string;
  submitTitle: string;

  welcomeAskBank: string;
  placeholderBank: string;
  placeholderAppendices: string;
  placeholderConfirmation: string;

  bankMissingFile: string;
  bankOnlyOneFile: string;
  onlyWordFiles: string;
  bankSameAsAppendix: (name: string) => string;
  bankReceivedAskAppendices: (name: string) => string;
  bankReplaced: (name: string, appendixCount: number) => string;
  askBankReplacement: string;

  appendicesAdded: (added: number, total: number) => string;
  nothingAdded: (total: number) => string;
  skippedNotWord: (names: string[]) => string;
  skippedSameAsBank: (names: string[]) => string;
  skippedDuplicates: (names: string[]) => string;
  skippedTooLarge: (names: string[]) => string;
  filesTooLarge: string;
  appendicesNextHint: string;
  appendicesTextHint: string;
  needAtLeastOneAppendix: string;
  appendixRemoved: (name: string, total: number) => string;

  doneUploading: string;
  analyzing: string;
  sectionsFallback: (sections: Array<{ title: string; existing_count: number }>) => string;
  confirmPrompt: (appendixCount: number) => string;
  confirmNextHint: string;
  confirmationTextHint: string;
  start: string;
  addMoreAppendices: string;
  addMorePrompt: string;

  buildStarting: string;
  buildDoneFallback: string;
  outputHint: string;
  outputHintTwoFiles: string;
  /** From the second version on, the hint names its version. */
  outputHintVersion: (version: number, twoFiles: boolean) => string;
  /** Titles of the file cards. */
  singleFileLabel: string;
  builtFileLabel: string;
  refinedFileLabel: string;
  fileDetail: (version: number) => string;
  download: string;
  downloadAria: (label: string, version: number) => string;
  defaultOutputFilename: string;
  defaultRefinedFilename: string;
  downloadPreparing: string;
  /** Shown on the file's own card for a few seconds; nothing is added to the chat. */
  downloadStarted: string;
  downloadOpenedInBrowser: string;
  downloadFailed: string;
  placeholderFollowup: string;
  followupNoFiles: string;
  reviseStarting: string;
  reviseDoneFallback: string;
  errorReviseGeneric: string;
  afterReviseError: string;
  warningsHeading: string;
  startOver: string;

  /** The progress of a run, stage by stage. */
  stageTitle: (stage: string) => string;
  stageProgress: (done: number, total: number) => string;
  activityDone: (kind: 'build' | 'revise', appendices: number, stages: number) => string;
  activityFailed: string;
  activityStopped: string;
  stop: string;
  placeholderRunning: string;
  /** While the server is stopping a run the lawyer asked to stop. */
  stopping: string;
  placeholderStopping: string;

  /** Saved conversations. */
  savedSessions: string;
  savedSessionsCount: (count: number) => string;
  newConversation: string;
  closePanel: string;
  currentSession: string;
  untitledSession: string;
  sessionsEmpty: string;
  sessionsNote: string;
  /** Said in the conversation when the server refused to keep it, before the server's own reason. */
  draftNotSaved: string;
  sessionStatus: (step: string, version: number, appendices: number) => string;
  /** What a conversation's row says while a run of it goes on in the server. */
  sessionRunning: (kind: string | null) => string;
  /** The history button, when a conversation is being worked on. */
  sessionsRunningCount: (count: number) => string;
  sessionWhen: (updatedAt: number, now: number) => string;
  openSession: (title: string) => string;
  deleteSession: (title: string) => string;
  confirmDelete: string;
  confirmDeleteYes: string;
  confirmDeleteNo: string;
  sessionOpenFailed: string;
  sessionsLoading: string;
  activityToggle: string;

  filesSummary: (total: number) => string;
  bankTag: string;
  replaceBank: string;
  removeFile: (name: string) => string;

  errorAnalyzeGeneric: string;
  errorBuildGeneric: string;
  errorUsageLimit: string;
  errorSignedOut: string;
  afterAnalyzeError: string;
  afterBuildError: string;
};

function appendixCountLabel(count: number): string {
  return count === 1 ? 'נספח שינויים אחד' : `${count} נספחי שינויים`;
}

function nameList(names: string[]): string {
  return names.map((n) => `"${n}"`).join(', ');
}

/** The stages the server reports, as the titles of the progress clusters. */
const STAGE_TITLES: Record<string, string> = {
  request: 'קריאת הבקשה',
  reading: 'קריאת נספחי השינויים',
  sorting: 'מיון לפי מספר סעיף',
  duplicates: 'בדיקת כפילויות',
  merging: 'כתיבת הבנק',
  checking: 'בקרת איכות',
  refining: 'התאמות בעותק השני',
  followup: 'סיכום ההחלטות שקיבלתי בדרך',
};

const COPY: BankOfChangesCopy = {
  backToWorkflows: 'תהליכי עבודה',
  working: 'עובד…',
  submitTitle: 'שליחה',

  welcomeAskBank:
    'שלום! אבנה עבורכם בנק שינויים מעודכן מתוך נספחי השינויים החתומים.\n' +
    'כדי להתחיל, צרפו בתיבה למטה את בנק השינויים הקיים — תבנית ריקה או הבנק במצבו הנוכחי (קובץ Word אחד).',
  placeholderBank: 'צרפו את קובץ בנק השינויים…',
  placeholderAppendices: 'צרפו נספחי שינויים…',
  placeholderConfirmation: 'צרפו נספחים נוספים, או כתבו "התחל"…',

  bankMissingFile:
    'כדי להמשיך אני צריך את קובץ בנק השינויים עצמו. צרפו אותו בעזרת סמל האטב שבתיבה למטה (קובץ Word אחד).',
  bankOnlyOneFile:
    'בשלב הזה אני צריך רק קובץ אחד — בנק השינויים הקיים. צרפו אותו לבדו, ואת נספחי השינויים תעלו מיד אחריו.',
  onlyWordFiles: 'אפשר להעלות קובצי Word בלבד (\u2066.docx\u2069 או \u2066.doc\u2069). צרפו את בנק השינויים כקובץ Word.',
  bankSameAsAppendix: (name) =>
    `הקובץ "${name}" כבר הועלה כנספח שינויים. צרפו את קובץ בנק השינויים עצמו, או הסירו אותו קודם מרשימת הנספחים.`,
  bankReceivedAskAppendices: (name) =>
    `קיבלתי את בנק השינויים "${name}".\n` +
    'עכשיו צרפו את כל נספחי השינויים שנחתמו עד היום — קובצי Word בלבד. אפשר לצרף כמה קבצים יחד, וגם בכמה הודעות.',
  bankReplaced: (name, appendixCount) =>
    `קיבלתי את בנק השינויים "${name}". ${appendixCountLabel(appendixCount)} שהעליתם קודם נשמרו.\n` +
    'אפשר לצרף נספחים נוספים, או ללחוץ על "סיימתי להעלות".',
  askBankReplacement: 'צרפו את קובץ בנק השינויים החדש (קובץ Word אחד). נספחי השינויים שהעליתם נשמרו.',

  appendicesAdded: (added, total) =>
    added === total
      ? `קיבלתי ${appendixCountLabel(total)}.`
      : `הוספתי ${appendixCountLabel(added)}. יש לי כעת ${appendixCountLabel(total)}.`,
  nothingAdded: (total) =>
    total === 0 ? 'לא נוסף אף נספח.' : `לא נוסף אף נספח חדש. יש לי ${appendixCountLabel(total)}.`,
  skippedNotWord: (names) => `אפשר להעלות קובצי Word בלבד (\u2066.docx\u2069 או \u2066.doc\u2069), ולכן לא צורפו: ${nameList(names)}.`,
  skippedSameAsBank: (names) =>
    `הקובץ ${nameList(names)} נושא את אותו שם כמו בנק השינויים, ולכן לא צורף כנספח.`,
  skippedDuplicates: (names) => `כבר הועלו קודם ולכן לא צורפו שוב: ${nameList(names)}.`,
  skippedTooLarge: (names) =>
    `הנפח הכולל של הקבצים גדול מדי למשלוח אחד, ולכן לא צורפו: ${nameList(names)}.`,
  filesTooLarge: 'הקובץ גדול מדי למשלוח. צרפו קובץ Word קטן יותר.',
  appendicesNextHint: 'אפשר לצרף נספחים נוספים, או ללחוץ על "סיימתי להעלות".',
  appendicesTextHint:
    'צרפו את נספחי השינויים בעזרת סמל האטב שבתיבה למטה. כשתסיימו, לחצו על "סיימתי להעלות".',
  needAtLeastOneAppendix: 'עדיין לא הועלה אף נספח שינויים. צרפו לפחות נספח אחד כדי להמשיך.',
  appendixRemoved: (name, total) =>
    total === 0
      ? `הסרתי את "${name}". לא נותרו נספחים — צרפו לפחות נספח אחד כדי להמשיך.`
      : `הסרתי את "${name}". יש לי כעת ${appendixCountLabel(total)}.`,

  doneUploading: 'סיימתי להעלות',
  analyzing: 'בודק את מבנה בנק השינויים ואת הנספחים שהעליתם…',
  sectionsFallback: (sections) =>
    sections.length === 0
      ? 'בדקתי את בנק השינויים.'
      : 'אלה הכותרות שמצאתי בבנק השינויים:\n' +
        sections.map((s) => `• ${s.title} — ${s.existing_count} שינויים קיימים`).join('\n'),
  confirmPrompt: (appendixCount) =>
    `יש לי ${appendixCountLabel(appendixCount)}. אם נשארו נספחים שעוד לא העליתם, אפשר לצרף אותם עכשיו.\n` +
    'כשתאשרו אתחיל לבנות את הבנק המעודכן — זה עשוי להימשך כמה דקות. להתחיל?',
  confirmNextHint: 'לחצו על "התחל" כשתהיו מוכנים.',
  confirmationTextHint: 'אפשר לצרף נספחים נוספים בעזרת סמל האטב, או ללחוץ על "התחל" כדי לבנות את הבנק.',
  start: 'התחל',
  addMoreAppendices: 'הוסף נספחים נוספים',
  addMorePrompt: 'צרפו את הנספחים הנוספים בעזרת סמל האטב שבתיבה למטה. כשתסיימו, לחצו על "התחל".',

  buildStarting: 'מתחיל לבנות את בנק השינויים…',
  buildDoneFallback: 'בנק השינויים המעודכן מוכן.',
  outputHint: 'הקובץ מוכן להורדה.',
  outputHintTwoFiles: 'שני קבצים מוכנים להורדה: הבנק כפי שנבנה, לפני ההתאמות, והבנק אחרי ההתאמות.',
  outputHintVersion: (version, twoFiles) =>
    twoFiles
      ? `גרסה ${version} מוכנה להורדה, בשני קבצים. הגרסאות הקודמות נשארות זמינות להורדה למעלה בשיחה.`
      : `גרסה ${version} של הבנק מוכנה להורדה. הגרסאות הקודמות נשארות זמינות להורדה למעלה בשיחה.`,
  singleFileLabel: 'בנק השינויים המעודכן',
  builtFileLabel: 'הבנק לפני ההתאמות',
  refinedFileLabel: 'הבנק אחרי ההתאמות',
  fileDetail: (version) => (version <= 1 ? 'DOCX' : `גרסה ${version} · DOCX`),
  download: 'הורדה',
  downloadAria: (label, version) => (version <= 1 ? `הורדה: ${label}` : `הורדה: ${label}, גרסה ${version}`),
  defaultOutputFilename: 'בנק שינויים מעודכן.docx',
  defaultRefinedFilename: 'בנק שינויים מעודכן - אחרי התאמות.docx',
  downloadPreparing: 'מכין…',
  downloadStarted: 'ההורדה התחילה',
  downloadOpenedInBrowser: 'ההורדה נפתחה בדפדפן',
  downloadFailed: 'לא הצלחתי להכין את הקובץ להורדה. אפשר לנסות שוב.',
  placeholderFollowup: 'כתבו כאן תשובה להחלטות, או כל תיקון שתרצו בבנק…',
  followupNoFiles:
    'בשלב הזה אי אפשר לצרף קבצים: הבנק נבנה מהקבצים שכבר הועלו. כתבו מה לשנות, או לחצו על "התחל מחדש" כדי לבנות בנק מקבצים אחרים.',
  reviseStarting: 'קורא את הבקשה…',
  reviseDoneFallback: 'סיימתי לטפל בבקשה.',
  errorReviseGeneric: 'התיקון לא הושלם — ייתכן שהחיבור לשרת נקטע.',
  afterReviseError: 'הגרסאות שכבר נוצרו נשארות זמינות להורדה למעלה. אפשר לשלוח את הבקשה שוב.',
  warningsHeading: 'לתשומת לבכם:',
  startOver: 'התחל מחדש',

  stageTitle: (stage) => STAGE_TITLES[stage] ?? 'עיבוד',
  stageProgress: (done, total) => `${done} מתוך ${total}`,
  activityDone: (kind, appendices, stages) => {
    const steps = stages === 1 ? 'בוצע שלב עיבוד אחד' : `בוצעו ${stages} שלבי עיבוד`;
    if (kind === 'revise') return `הבקשה טופלה, ${steps}`;
    return appendices > 0 ? `נקראו ${appendices === 1 ? 'נספח אחד' : `${appendices} נספחים`}, ${steps}` : steps;
  },
  activityFailed: 'העיבוד נעצר לפני שהסתיים',
  activityStopped: 'העיבוד נעצר',
  stop: 'עצירה',
  placeholderRunning: 'הסוכן עובד… אפשר לעצור בכפתור העצירה, או לסגור ולחזור: העיבוד ממשיך בשרת',
  stopping: 'עוצר…',
  placeholderStopping: 'עוצר את העיבוד…',

  savedSessions: 'שיחות שמורות',
  savedSessionsCount: (count) => (count === 1 ? 'שיחה שמורה אחת' : `${count} שיחות שמורות`),
  newConversation: 'שיחה חדשה',
  closePanel: 'סגירה',
  currentSession: 'השיחה הנוכחית',
  untitledSession: 'שיחה ללא שם',
  sessionsEmpty: 'עדיין אין שיחות שמורות. שיחה נשמרת מרגע שמצרפים אליה קובץ, ואפשר לחזור אליה מכאן או מהצ\'אט שבאתר.',
  sessionsNote: 'אפשר לראות את השיחות ולהמשיך אותן גם בצ\'אט שבאתר, תחת Workflows.',
  draftNotSaved: 'השיחה לא נשמרה בשרת:',
  sessionStatus: (step, version, appendices) => {
    if (step === 'done') return version <= 1 ? 'הבנק מוכן' : `גרסה ${version} מוכנה`;
    if (step === 'awaiting_confirmation') return 'מוכן לבנייה';
    if (step === 'awaiting_bank') return 'ממתין לבנק השינויים';
    return appendices === 0 ? 'ממתין לנספחים' : appendices === 1 ? 'נספח אחד הועלה' : `${appendices} נספחים הועלו`;
  },
  sessionRunning: (kind) =>
    kind === 'analyze' ? 'בודק את הקבצים…' : kind === 'build' ? 'בונה את הבנק…' : kind === 'revise' ? 'מבצע תיקון…' : 'בעיבוד…',
  sessionsRunningCount: (count) => (count === 1 ? 'שיחה אחת בעיבוד כרגע' : `${count} שיחות בעיבוד כרגע`),
  sessionWhen: (updatedAt, now) => {
    const minutes = Math.floor((now - updatedAt) / 60000);
    if (minutes < 1) return 'עכשיו';
    if (minutes < 60) return minutes === 1 ? 'לפני דקה' : `לפני ${minutes} דקות`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours === 1 ? 'לפני שעה' : hours === 2 ? 'לפני שעתיים' : `לפני ${hours} שעות`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'אתמול';
    if (days < 7) return `לפני ${days} ימים`;
    const date = new Date(updatedAt);
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  },
  openSession: (title) => `פתיחת השיחה "${title}"`,
  deleteSession: (title) => `מחיקת השיחה "${title}"`,
  confirmDelete: 'למחוק את השיחה ואת הקבצים שלה?',
  confirmDeleteYes: 'מחיקה',
  confirmDeleteNo: 'ביטול',
  sessionOpenFailed: 'לא הצלחתי לפתוח את השיחה הזאת. ייתכן שנמחקה, או שהחיבור לשרת נקטע.',
  sessionsLoading: 'טוען…',
  activityToggle: 'הצגה או הסתרה של שלבי העיבוד',

  filesSummary: (total) => `הקבצים שהועלו (${total})`,
  bankTag: 'בנק השינויים',
  replaceBank: 'החלף',
  removeFile: (name) => `הסר את ${name}`,

  errorAnalyzeGeneric: 'לא הצלחתי לבדוק את הקבצים — ייתכן שהחיבור לשרת נקטע.',
  errorBuildGeneric: 'בניית בנק השינויים לא הושלמה — ייתכן שהחיבור לשרת נקטע.',
  errorUsageLimit: 'הגעתם למגבלת השימוש בחשבון, ולכן אי אפשר להמשיך כרגע.',
  errorSignedOut: 'החיבור לחשבון פג ולא הצלחתי לחדש אותו, ולכן הבקשה לא נשלחה. התחברו מחדש לחשבון.',
  afterAnalyzeError:
    'הקבצים שהעליתם נשמרו. אפשר להסיר או להחליף קובץ ברשימת הקבצים, לצרף קבצים נוספים, וללחוץ שוב על "סיימתי להעלות".',
  afterBuildError:
    'הקבצים שהעליתם נשמרו. אפשר ללחוץ שוב על "התחל", או לשנות קודם את רשימת הקבצים.',
};

export function getBankOfChangesCopy(): BankOfChangesCopy {
  return COPY;
}
